import { useEffect } from "react";
// Real icons, not hand-drawn paths. The first pass approximated a tag and a
// repeat arrow by hand and both read as slightly-wrong shapes at 18px — which is
// exactly the size at which a wrong shape is all you notice. Heroicons is already
// a dependency (CourseViews, MembershipViews), so this adds nothing to the bundle
// that was not already in it.
import { TagIcon, ArrowPathIcon, SparklesIcon } from "@heroicons/react/24/solid";
import { ArrowRightIcon, LockClosedIcon } from "@heroicons/react/24/outline";
import { withReferral } from "./affiliateReferral";

/**
 * "Choose your plan" — the offers behind one course.
 *
 * A course can be sold by several products at once: it only has to sit in each
 * one's bundle. The card carries a single `sales_url`, so before this existed a
 * click went to whichever product the API resolved first — the oldest one — and
 * the rest were unreachable from inside the membership however the creator had
 * set them up. This lists them all and lets the member pick.
 *
 * Shown only when there are two or more; a single offer still opens directly,
 * because a popup with one choice in it is not a choice.
 */

const Icon = {
  tag: (cls) => <TagIcon className={cls} />,
  arrow: (cls) => <ArrowRightIcon className={cls} strokeWidth={2.4} />,
  spark: (cls) => <SparklesIcon className={cls} />,
  repeat: (cls) => <ArrowPathIcon className={cls} />,
  lock: (cls) => <LockClosedIcon className={cls} strokeWidth={2} />,
};

// Currency symbols we render inline. Mirrors CourseCheckout::CURRENCY_SYMBOLS on
// the admin side so the price on this card and the price on the checkout it opens
// are written the same way.
const SYMBOLS = { USD: "$", INR: "₹", EUR: "€", GBP: "£", AUD: "A$", CAD: "C$" };

// The platform's frequency vocabulary carries the parent's long-standing typo
// ("quaterly"), which is fine in storage but should not reach a buyer's eye.
const FREQUENCY_LABELS = {
  quaterly: "quarter",
  quarterly: "quarter",
  monthly: "month",
  yearly: "year",
  weekly: "week",
  daily: "day",
};

export function offerMoney(amount, currency) {
  const n = Number(amount || 0);
  const code = String(currency || "").toUpperCase();
  // Whole amounts drop the ".00" — a price card has no room for noise — but both
  // forms group the thousands, so ₹1,499 and ₹1,499.50 don't sit differently.
  const text = n.toLocaleString(undefined, {
    minimumFractionDigits: n % 1 === 0 ? 0 : 2,
    maximumFractionDigits: 2,
  });
  const symbol = SYMBOLS[code];
  return symbol ? `${symbol}${text}` : `${text}${code ? ` ${code}` : ""}`;
}

/**
 * A priced offer sorts by its price; one whose price did not resolve sorts last.
 *
 * Price 0 is two different things: a genuinely FREE plan (a $0 product is how a
 * creator hands out a free course), which belongs at the top, and "no pricing row
 * answered", which has nothing to say and belongs at the bottom. `pricing_type`
 * is what tells them apart.
 */
const rank = (o) =>
  Number(o.price) > 0 || o.pricing_type === "free" ? Number(o.price) || 0 : Infinity;

/**
 * The cheapest offer of a set, or null when none of them resolved a price.
 *
 * Callers that show ONE headline price for a course that has several offers use
 * this for a "From ₹1,499" line. Quoting any other offer's price — or the
 * course's own stored one, which belongs to whichever product was wired first —
 * tells the member a number they may not be able to pay.
 */
export function cheapestOffer(offers = []) {
  const priced = offers.filter((o) => rank(o) !== Infinity);
  if (!priced.length) return null;
  return priced.reduce((a, b) => (rank(b) < rank(a) ? b : a));
}

export default function CourseOfferModal({ courseName, offers = [], primary = "#0274F7", onClose }) {
  // Escape closes, like every other panel in the app. Registered unconditionally
  // so the hook order never depends on whether there are offers to show.
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose?.();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  if (!offers.length) return null;

  // Cheapest first, so the list reads as a ladder rather than in the order the
  // creator happened to create the products.
  const byPrice = [...offers].sort((a, b) => rank(a) - rank(b));
  // The creator's pick (course settings → "Show popup, recommend one"): it leads
  // the list and takes the highlight, in place of the automatic cheapest-first
  // "Best value" — the creator knows which plan they want to push.
  const recommended = offers.find((o) => o.recommended) || null;
  const sorted = recommended ? [recommended, ...byPrice.filter((o) => o !== recommended)] : byPrice;
  // Only worth calling out when the prices actually differ — "Best value" on the
  // first of three identically priced plans is noise.
  const bestValue = !recommended && rank(sorted[0]) !== Infinity && rank(sorted[0]) < rank(sorted[1] ?? {})
    ? sorted[0]
    : null;
  const featuredOffer = recommended || bestValue;

  const open = (offer) => {
    // New tab, matching every other place a course sends a member to an offer
    // (the course grid, the instructor page, the members gate). noopener/noreferrer
    // because the destination is a creator-supplied URL.
    if (offer.url) window.open(withReferral(offer.url), "_blank", "noopener,noreferrer");
    onClose?.();
  };

  return (
    <div
      className="fixed inset-0 z-[60] flex items-stretch justify-center bg-ink/70 p-0 backdrop-blur-xl sm:items-center sm:p-4"
      role="dialog"
      aria-modal="true"
      aria-label="Choose your plan"
    >
      <style>{`
        @keyframes offersPop { from { opacity:0; transform: translateY(20px) scale(.97) } to { opacity:1; transform: translateY(0) scale(1) } }
        @keyframes offersRise { from { opacity:0; transform: translateY(10px) } to { opacity:1; transform: translateY(0) } }
      `}</style>

      <div className="relative flex h-full max-h-none w-full flex-col overflow-hidden rounded-none bg-surface shadow-[0_40px_80px_-20px_rgba(0,0,0,.55)] animate-[offersPop_.34s_cubic-bezier(.22,1,.36,1)] sm:h-auto sm:max-h-[92vh] sm:max-w-[30rem] sm:rounded-[28px] sm:ring-1 sm:ring-black/10 dark:sm:ring-white/10">

        {/*
          ── Header ──────────────────────────────────────────────────────────
          A LIGHT gradient in the membership's colour, fading out into the
          surface. Not the saturated slab of the first pass: filling the top
          third with full-strength brand made the accent the loudest thing on a
          panel whose whole job is letting three prices be compared, and forced
          white type that then had to fight its own background. At this strength
          the header is unmistakably branded and the type stays dark and quiet.
        */}
        <div className="relative shrink-0 overflow-hidden bg-surface px-6 pb-6 pt-7 text-center">
          {/* A LIGHT wash of the membership's colour, fading into the surface —
              enough that the header reads as branded, nowhere near enough to
              make the type fight it. Mixed against the surface token rather than
              laid over it, so the same percentages work in dark mode. */}
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-0 z-0"
            style={{
              background: `linear-gradient(180deg,
                color-mix(in srgb, ${primary} 18%, rgb(var(--c-surface))) 0%,
                color-mix(in srgb, ${primary} 9%, rgb(var(--c-surface))) 52%,
                rgb(var(--c-surface)) 100%)`,
            }}
          />
          {/* A softer brand bloom behind the icon, for depth rather than colour. */}
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-0 z-0"
            style={{ background: `radial-gradient(70% 90% at 50% 0%, ${primary}26 0%, transparent 70%)` }}
          />

          <button
            onClick={onClose}
            aria-label="Close"
            className="absolute right-4 top-4 z-20 flex h-8 w-8 items-center justify-center rounded-full text-gray-400 transition hover:bg-gray-100 hover:text-gray-600"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" className="h-4 w-4">
              <path d="M18 6 6 18M6 6l12 12" />
            </svg>
          </button>

          <div className="relative z-10">
            {/* A clean surface tile on the wash, so the glyph reads crisply —
                a tinted tile on a tinted header would disappear into it. */}
            <div
              className="mx-auto flex h-12 w-12 items-center justify-center rounded-2xl bg-surface shadow-sm ring-1 ring-black/5 dark:ring-white/10"
              style={{ color: primary }}
            >
              {Icon.tag("w-6 h-6")}
            </div>
            <h3 className="mt-3.5 text-[19px] font-extrabold leading-tight tracking-tight text-gray-900">
              Choose your plan
            </h3>
            <p className="mx-auto mt-1 max-w-[22rem] truncate text-[13px] font-medium text-gray-500">
              {sorted.length} ways to get {courseName || "this course"}
            </p>
          </div>

          {/* Hairline that carries the brand instead of a whole panel doing it. */}
          <div
            aria-hidden="true"
            className="absolute inset-x-0 bottom-0 h-px"
            style={{ background: `linear-gradient(90deg, transparent, ${primary}59, transparent)` }}
          />
        </div>

        {/* ── Plans ─────────────────────────────────────────────────────────── */}
        <div className="flex-1 overflow-y-auto scrollbar-hide bg-surface-sunken px-4 py-5 sm:px-5">
          <ul className="space-y-3">
            {sorted.map((o, i) => {
              const isSub = o.pricing_type === "subscription";
              const cycle = FREQUENCY_LABELS[String(o.frequency || "").toLowerCase()];
              const featured = !!featuredOffer && o === featuredOffer;
              return (
                <li
                  key={o.product_id || `${o.url}-${i}`}
                  className="animate-[offersRise_.4s_cubic-bezier(.22,1,.36,1)_both]"
                  style={{ animationDelay: `${60 + i * 55}ms` }}
                >
                  {/* The whole plan is the target — a small button beside a big
                      card makes the member hunt for the hit area. */}
                  <button
                    type="button"
                    onClick={() => open(o)}
                    className="group relative w-full overflow-hidden rounded-2xl bg-surface p-[1px] text-left transition duration-200 hover:-translate-y-[3px] focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
                    style={{
                      // A hairline, not a filled border: brand on the featured
                      // plan at low strength, neutral elsewhere until hover.
                      background: featured
                        ? `color-mix(in srgb, ${primary} 55%, rgb(var(--c-gray-200)))`
                        : "rgb(var(--c-gray-200))",
                      boxShadow: "0 2px 10px -6px rgba(0,0,0,.25)",
                      // Read by the group-hover accent on the arrow below.
                      "--offer-accent": primary,
                    }}
                  >
                    <span
                      aria-hidden="true"
                      className="pointer-events-none absolute inset-0 rounded-2xl opacity-0 transition-opacity duration-200 group-hover:opacity-100"
                      style={{ background: `color-mix(in srgb, ${primary} 70%, rgb(var(--c-gray-200)))` }}
                    />

                    <span className="relative flex items-stretch gap-3 rounded-[15px] bg-surface p-4">
                      {/* Neutral glyph tile — an anchor for the row, not another
                          thing competing for attention. Only the featured plan
                          tints it, and softly. */}
                      <span
                        className="flex h-11 w-11 shrink-0 items-center justify-center self-center rounded-xl transition-colors duration-200"
                        style={{
                          background: featured
                            ? `color-mix(in srgb, ${primary} 13%, rgb(var(--c-surface)))`
                            : "rgb(var(--c-gray-100))",
                          color: featured ? primary : "rgb(var(--c-gray-500))",
                        }}
                      >
                        {isSub ? Icon.repeat("w-[18px] h-[18px]") : Icon.tag("w-[19px] h-[19px]")}
                      </span>

                      <span className="min-w-0 flex-1 self-center">
                        <span className="block truncate text-[14.5px] font-bold leading-tight text-gray-900">
                          {o.name}
                        </span>
                        {/* Badge rides on the META line, not the title. Beside the
                            title it wrapped on any name long enough to need it,
                            which made the one highlighted card taller than the
                            others — the opposite of what a highlight should do. */}
                        <span className="mt-1.5 flex items-center gap-1.5 text-[11.5px] font-semibold text-gray-500">
                          {featured && (
                            <span
                              className="shrink-0 rounded-full px-1.5 py-[2px] text-[9px] font-black uppercase tracking-[0.07em]"
                              style={{
                                background: `color-mix(in srgb, ${primary} 16%, rgb(var(--c-surface)))`,
                                color: primary,
                              }}
                            >
                              {recommended ? "Recommended" : "Best value"}
                            </span>
                          )}
                          {isSub ? Icon.repeat("w-3 h-3 shrink-0 opacity-60") : Icon.spark("w-3 h-3 shrink-0 opacity-60")}
                          <span className="truncate">
                            {isSub
                              ? `Recurring${cycle ? ` · billed every ${cycle}` : ""}`
                              : "One-time payment"}
                          </span>
                        </span>
                      </span>

                      {/* Price block, right-aligned so the column of numbers can be
                          compared down the list at a glance. */}
                      <span className="flex shrink-0 items-center gap-2.5 self-center pl-1">
                        <span className="text-right">
                          {o.price > 0 ? (
                            <>
                              <span className="block text-[19px] font-black leading-none tracking-tight text-gray-900">
                                {offerMoney(o.price, o.currency)}
                              </span>
                              {isSub && cycle && (
                                <span className="mt-1 block text-[10.5px] font-bold uppercase tracking-wide text-gray-400">
                                  per {cycle}
                                </span>
                              )}
                            </>
                          ) : o.pricing_type === "free" ? (
                            // A $0 plan is a real answer, not a missing one — it is how
                            // a creator hands out a course.
                            <span className="block text-[19px] font-black leading-none tracking-tight text-gray-900">
                              Free
                            </span>
                          ) : (
                            // No pricing row answered — say so plainly rather than print
                            // "0". The page it opens carries the real terms.
                            <span className="block text-[11px] font-semibold leading-tight text-gray-400">
                              See page<br />for pricing
                            </span>
                          )}
                        </span>
                        {/* The one solid brand element in the panel, and only
                            while the row is hovered — so the accent marks the
                            plan being chosen rather than decorating all three at
                            rest. Driven by a CSS variable set on the button, so
                            hovering ANYWHERE on the card lights it. */}
                        <span className="flex h-8 w-8 items-center justify-center rounded-full bg-gray-100 text-gray-500 transition-all duration-200 group-hover:translate-x-0.5 group-hover:bg-[var(--offer-accent)] group-hover:text-[color:var(--brand-btn-text,#fff)]">
                          {Icon.arrow("w-[15px] h-[15px]")}
                        </span>
                      </span>
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        </div>

        {/* ── Footer ────────────────────────────────────────────────────────── */}
        <div className="flex shrink-0 items-center justify-end gap-3 border-t border-gray-200/70 bg-surface px-5 py-3.5">
          {/* <span className="inline-flex items-center gap-1.5 text-[11.5px] font-semibold text-gray-400">
            {Icon.lock("w-3.5 h-3.5 shrink-0")}
            Opens the offer in a new tab
          </span> */}
          <button
            type="button"
            onClick={onClose}
            className="rounded-xl px-4 py-2 text-[13px] font-bold text-gray-500 transition hover:bg-gray-100 hover:text-gray-700"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
